import { useEffect, useState } from "react";
import { FileText, Plus, Trash2, Eye, Star, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const TYPE_LABEL = { msa: "MSA", sow: "SOW", change_order: "Change Order", quotation: "Quotation" };

export default function ContractTemplates() {
  const { activeWorkspaceId } = useAuth();
  const [type, setType] = useState("msa");
  const [templates, setTemplates] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [preview, setPreview] = useState(null); // {tpl, html}

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/contract-templates`, {
        params: { contract_type: type },
      });
      setTemplates(data.templates || []);
      setTokens(data.available_tokens || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, type]);

  const del = async (tid) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/contract-templates/${tid}`); load(); }
    catch { toast.error("Failed"); }
  };

  const setDefault = async (tid) => {
    try { await api.patch(`/contract-templates/${tid}`, { is_default: true }); load(); toast.success("Set as default"); }
    catch { toast.error("Failed"); }
  };

  const doPreview = async (tpl) => {
    try {
      const ctx = tokens.reduce((acc, t) => { acc[t] = `<${t}>`; return acc; }, {});
      const { data } = await api.post(`/contract-templates/${tpl.id}/render`, ctx);
      setPreview({ tpl, html: data.rendered_html });
    } catch { toast.error("Preview failed"); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="templates-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase text-muted-foreground tracking-wide flex items-center gap-1.5">
            <FileText className="w-3 h-3" /> Estimates & Contracts
          </div>
          <h1 className="text-3xl font-bold mt-0.5 tracking-tight">Contract Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Save reusable MSA / SOW / Change Order / Quotation templates with placeholder tokens like <code className="text-[10px] bg-muted px-1">{"{{client_name}}"}</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5" data-testid="tpl-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button type="button" onClick={() => setShowNew(true)} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1.5 font-medium" data-testid="tpl-new-btn">
            <Plus className="w-3.5 h-3.5" /> New Template
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {["msa", "sow", "change_order", "quotation"].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setType(t)}
            className={`text-xs px-3 py-1.5 border transition ${type === t ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
            data-testid={`tpl-type-${t}`}
          >
            {TYPE_LABEL[t]}
          </button>
        ))}
      </div>

      <div className="border border-border">
        <div className="px-4 py-2 border-b border-border bg-muted/40 flex items-center justify-between gap-2">
          <div className="text-xs font-semibold uppercase tracking-wide">{TYPE_LABEL[type]} templates ({templates.length})</div>
          <div className="text-[10px] text-muted-foreground">
            Available tokens: {tokens.map((t) => <code key={t} className="mx-0.5 bg-muted px-1">{`{{${t}}}`}</code>)}
          </div>
        </div>
        <div className="divide-y divide-border">
          {templates.map((t) => (
            <div key={t.id} className="px-4 py-3 hover:bg-muted/20 flex items-start justify-between gap-3" data-testid={`tpl-row-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{t.name}</span>
                  {t.is_default && <span className="text-[10px] px-1.5 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"><Star className="w-2.5 h-2.5" /> default</span>}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2" dangerouslySetInnerHTML={{ __html: (t.body_html || "").slice(0, 200).replace(/<[^>]+>/g, " ") }} />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => doPreview(t)} className="text-[10px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1" data-testid={`tpl-preview-${t.id}`}>
                  <Eye className="w-3 h-3" /> Preview
                </button>
                {!t.is_default && (
                  <button type="button" onClick={() => setDefault(t.id)} className="text-[10px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1" data-testid={`tpl-default-${t.id}`}>
                    <Star className="w-3 h-3" /> Set default
                  </button>
                )}
                <button type="button" onClick={() => del(t.id)} className="text-[10px] p-1 text-muted-foreground hover:text-rose-500" data-testid={`tpl-delete-${t.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
          {templates.length === 0 && !loading && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">No {TYPE_LABEL[type]} templates yet. Click New Template to create one.</div>
          )}
        </div>
      </div>

      {showNew && <NewTemplateDialog wsId={activeWorkspaceId} defaultType={type} tokens={tokens} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); toast.success("Template created"); }} />}
      {preview && <PreviewDialog tpl={preview.tpl} html={preview.html} onClose={() => setPreview(null)} />}
    </div>
  );
}

function NewTemplateDialog({ wsId, defaultType, tokens, onClose, onCreated }) {
  const [type, setType] = useState(defaultType);
  const [name, setName] = useState("");
  const [body, setBody] = useState("<h1>Agreement between {{workspace_name}} and {{client_name}}</h1>\n<p>Effective {{start_date}} — {{end_date}}</p>");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post(`/workspaces/${wsId}/contract-templates`, {
        contract_type: type,
        name: name.trim(),
        body_html: body,
      });
      onCreated();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="tpl-new-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-background border border-border shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-border bg-muted/40 font-semibold text-sm">New template</div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Type</label>
              <select value={type} onChange={(e) => setType(e.target.value)} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border">
                {["msa", "sow", "change_order", "quotation"].map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full text-sm px-2 py-1.5 mt-0.5 bg-background border border-border" data-testid="tpl-new-name" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Body HTML (use {"{{token}}"} placeholders)</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border font-mono" data-testid="tpl-new-body" />
          </div>
          <div className="text-[10px] text-muted-foreground">
            Tokens: {tokens.map((t) => <code key={t} className="mx-0.5 bg-muted px-1 cursor-pointer" onClick={() => setBody((b) => b + ` {{${t}}}`)}>{`{{${t}}}`}</code>)}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground disabled:opacity-50 font-medium" data-testid="tpl-new-save">
            {saving ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewDialog({ tpl, html, onClose }) {
  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="tpl-preview-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-background border border-border shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-border bg-muted/40 flex items-center justify-between">
          <div className="font-semibold text-sm">Preview: {tpl.name}</div>
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Close</button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 prose max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  );
}
